import { useEffect, useState } from "react";
import { getTenant, isFreeStack } from "./tenant";
import { useQuery } from "@tanstack/react-query";
import { getCreditUsage, getUserList } from "./apis";
import { GET_USER_LIST } from "../services/constants";
import { getUserById, getUserByUuid, formatUserName } from "./utils";
import { BaseUser, User } from "@/types";

// Re-export the TypeScript hooks
export { useTenantInfo } from "./hooks/useTenantInfo";
export { useActiveUserOptions } from "./hooks/useActiveUserOptions";

export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useBoolean = (defaultValue = false) => {
  const [value, setValue] = useState(defaultValue);
  return {
    value,
    toggle: () => setValue(v => !v),
    setTrue: () => setValue(true),
    setFalse: () => setValue(false),
  };
};

const isKeyOfUser = (key: string, obj: BaseUser): key is keyof BaseUser => key in obj;

export const useUsers = (allUsers = false, includeDeleted = false) => {
  const tenant = getTenant();
  const { data: users, isLoading } = useQuery({
    queryKey: [GET_USER_LIST, tenant, allUsers, includeDeleted],
    queryFn: () =>
      getUserList({
        company: tenant,
        with_service_user: false,
        allUsers,
        include_deleted: includeDeleted,
      }),
    gcTime: 300000,
  });

  return {
    users: users?.users,
    getById: (id: number) => getUserById(users?.users, id),
    getByUuid: (uuid: string) => getUserByUuid(users?.users, uuid),
    getFormattedUserNameById: (id: number | null | undefined, fallbackField = "Unknown") => {
      if (!id) return null;
      const user = getUserById(users?.users, id);
      // Return null for service users if fallbackField is not "Unknown"
      if (!user) return fallbackField === "Unknown" ? "Unknown" : null;

      const formattedName = formatUserName(user, "");
      if (formattedName) return formattedName;

      // If no formatted name, check if fallbackField is a user property
      if (isKeyOfUser(fallbackField, user)) {
        const value = user[fallbackField];
        return value ? String(value) : "Unknown";
      }
      return "Unknown";
    },
    isLoading,
  };
};

export const useUsersWithServiceUser = () => {
  const withServiceUser = true;
  const tenant = getTenant();
  const { data: users } = useQuery({
    queryKey: [GET_USER_LIST, tenant, withServiceUser],
    queryFn: () => getUserList({ company: tenant, with_service_user: withServiceUser }),
    gcTime: 300000,
  });
  return { users: users?.users, getById: (id: User["id"]) => getUserById(users?.users, id) };
};

export const useUsersWithServiceUserWithMapping = () => {
  const withServiceUser = true;
  const tenant = getTenant();

  const { data: users } = useQuery({
    queryKey: [GET_USER_LIST, tenant, withServiceUser],
    queryFn: () => getUserList({ company: tenant, with_service_user: withServiceUser }),
    gcTime: 300000,
  });

  const filteredUsers = users?.users?.filter(user => user.first_name && user.last_name) || [];

  const userNameToID = filteredUsers.reduce(
    (map, user) => {
      map[`${user.first_name} ${user.last_name}`] = user.id;
      return map;
    },
    {} as Record<string, number>
  );

  const userIDtoName = filteredUsers.reduce(
    (map, user) => {
      map[user.id] = `${user.first_name} ${user.last_name}`;
      return map;
    },
    {} as Record<number, string>
  );

  return {
    users: filteredUsers,
    getById: (id: User["id"]) => getUserById(filteredUsers, id),
    userNameToID,
    userIDtoName,
  };
};

const GET_CREDIT_USAGE_KEY = "get-credit-usage";

export const useIsEnterpriseUser = () => {
  const isFreeUser = isFreeStack();
  const { data } = useQuery({
    queryKey: [GET_CREDIT_USAGE_KEY],
    queryFn: getCreditUsage,
    enabled: isFreeUser,
  });
  if (!isFreeUser) return { isEnterpriseUser: true };
  return { isEnterpriseUser: data?.current_edition === "Enterprise" };
};

const SESSION_KEY = "sessionId";
const EXPIRY_KEY = "sessionExpiry";
const SESSION_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
const RENEWAL_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds

function generateSessionId() {
  return "_" + Math.random().toString(36).substr(2, 9) + "_saas";
}

function createNewSession() {
  const newSessionId = generateSessionId();
  const newExpiry = new Date().getTime() + SESSION_DURATION;
  localStorage.setItem(SESSION_KEY, newSessionId);
  localStorage.setItem(EXPIRY_KEY, newExpiry.toString());
  return newSessionId;
}

export function useSession() {
  const [sessionId, setSessionId] = useState(() => {
    const storedSessionId = localStorage.getItem(SESSION_KEY);
    const storedExpiry = localStorage.getItem(EXPIRY_KEY);
    if (storedSessionId && storedExpiry && new Date().getTime() < parseInt(storedExpiry, 10)) {
      return storedSessionId;
    }
    return createNewSession();
  });

  const generateNewSession = () => {
    const newSessionId = createNewSession();
    setSessionId(newSessionId);
    return newSessionId;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const storedExpiry = localStorage.getItem(EXPIRY_KEY);
      if (storedExpiry && new Date().getTime() > parseInt(storedExpiry, 10) - RENEWAL_THRESHOLD) {
        generateNewSession();
      }
    }, RENEWAL_THRESHOLD);

    return () => clearInterval(interval);
  }, []);

  return sessionId;
}
